import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  process,
  SortDescriptor,
  State,
} from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridItemChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { QueryObserverResult, useQueryClient } from '@tanstack/react-query';
import { get } from 'lodash';
import {
  FC,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IsSupplierUser } from 'src/constants/permissionsList';
import { ProductFilterCategories } from 'src/constants/productFilterCategories';
import { queryKeys } from 'src/constants/querykeys';
import useConfirm from 'src/hooks/useConfirm';
import usePermission from 'src/hooks/usePermissions';
import usePostMessage, { handlePostMessage } from 'src/hooks/usePostMessage';
import UnsavedSelectionDialog from 'src/sections/catalog_products/UnsavedSelectionDialog';
import { PRESERVE_SORT_FIELDS } from 'src/sections/catalog_products/ViewCatalogProductsContext';
import useUnsavedSelection from 'src/sections/catalog_products/useUnsavedSelection';
import { DEFAULT_SEARCH_CATALOG_OPTION } from 'src/sections/search_products/searchProductContext.utils';
import { SearchCatalogOption } from 'src/sections/search_products/searchProductData';
import useUpdateCartMutation from 'src/services/cart/useUpdateCartMutation';
import { fetchProductDetailsQuery } from 'src/services/catalog_products/productDetailsQueryFn';
import useGetCatalogsQuery from 'src/services/catalog_products/useGetCatalogsQuery';
import useGetProductDetailsQuery from 'src/services/catalog_products/useGetProductDetailsQuery';
import { fetchShoppingListsForExport } from 'src/services/favorite/fetchShoppingListsForExport';
import useCopyFavoriteProductsMutation from 'src/services/favorite/useCopyFavoriteProductsMutation';
import useDeleteFavoriteProductsMutation from 'src/services/favorite/useDeleteFavoriteProductsMutation';
import useGetAllFavoriteFoldersQuery from 'src/services/favorite/useGetAllFavoriteFoldersQuery';
import useGetFavoriteFoldersQuery from 'src/services/favorite/useGetFavoriteFoldersQuery';
import useGetShoppingListQuery, {
  removeInactiveShoppingListQueries,
  ShoppingListQueryResult,
} from 'src/services/favorite/useGetShoppingListMutatation';
import useMoveFavoriteProductsMutation from 'src/services/favorite/useMoveFavoriteProductsMutation';
import useUpdateCompanySpecifiedMutation from 'src/services/favorite/useUpdateCompanySpecifiedMutation';
import useUpdateFavoriteProductMutation from 'src/services/favorite/useUpdateFavoriteProductMutation';
import { CartItem } from 'src/types/cart';
import { CatalogTreeViewItem } from 'src/types/catalog/Catalog';
import { ProductDetails } from 'src/types/catalog/ProductDetails';
import { GetShoppingListRequest } from 'src/types/faviriteProducts/getShoppingList';
import { FavoriteFolder } from 'src/types/favorite';
import { DropDownCategories, SetState } from 'src/types/generic';
import { RecommendPageParams } from 'src/types/recommendation_items';
import { ShoppingListOption, ShoppingListProduct } from 'src/types/shopping_lists/ShoppingList';
import { exportShoppingListsToExcel } from 'src/utils/exportShoppingListsToExcel';
import { getCartItemProductKey, removeProductSelection } from 'src/utils/productActionMappers';
import {
  getCatalogDropdownPlaceholder,
  transformCatalogsToTreeData,
} from 'src/utils/tansformCatalogsToTreeData';
import { createContext } from 'use-context-selector';
import {
  applyAttributesFilter,
  applySearchTextFilter,
  applyToolbarFilters,
  buildReorderFavoriteProductRequest,
  buildShoppingListProcessState,
  buildUpdateFavoriteProductRequest,
  EMPTY_GRID_FILTER,
  extractAttributesFilter,
  getAttributesFilterKey,
  isShoppingListParEditable,
  prepareProductsForGrid,
  removeFieldFromFilter,
  shouldClearAttributesForContractedItems,
  shouldResetItemFilterForAttributes,
  type GridReadyShoppingListProduct,
} from './shoppingListProductMapper';
import { isSortOrderAscending, reorderShoppingListProducts } from './shoppingListReorder';
import { sortProductsForExport } from './utils/exportShoppingListsToExcel';

export interface ShoppingListProductSelectionState {
  selected: boolean;
  qty: number;
}

export const initialListViewDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'sortOrder', dir: 'asc' }],
  filter: { logic: 'and', filters: [] },
};

export const initialPictureViewDataState: State = {
  skip: 0,
  take: 25,
  sort: [{ field: 'sortOrder', dir: 'asc' }],
  filter: { logic: 'and', filters: [] },
};

export const SHOPPING_LIST_PRESERVE_SORT_FIELDS = [...PRESERVE_SORT_FIELDS, 'sortOrder'];

export interface ShoppingListProductsContextShape {
  listId: number;
  shoppingList: FavoriteFolder | undefined;
  shoppingListFolders: FavoriteFolder[];
  selectShoppingList: (folder: FavoriteFolder) => void;
  selectedView: number;
  setSelectedView: SetState<number>;
  dataState: State;
  gridDataState: State;
  setDataState: SetState<State>;
  onDataStateChange: (event: GridDataStateChangeEvent | State) => Promise<void>;
  products: ShoppingListProduct[];
  /** Filtered products before sort/pagination — for client-side CardGrid processing. */
  sourceProducts: GridReadyShoppingListProduct[];
  productsTotal: number;
  loadingProducts: boolean;
  filterSearch: string;
  handleFilterSearchChange: (value: string) => Promise<void>;
  productSelections: Record<string, ShoppingListProductSelectionState>;
  setProductSelections: SetState<Record<string, ShoppingListProductSelectionState>>;
  onSelectionChange: (event: GridSelectionChangeEvent) => void;
  onItemChange: (event: GridItemChangeEvent) => void;
  getProductSelection: (productKey: string) => ShoppingListProductSelectionState;
  updateProductSelection: (
    productKey: string,
    updates: Partial<ShoppingListProductSelectionState>
  ) => void;
  hasSelectedItems: boolean;
  clearSelections: () => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  getTakeForView: (viewIndex: number) => number;
  getSortForView: (viewIndex: number) => Array<SortDescriptor>;
  shouldPreserveSort: (currentSortArray: Array<SortDescriptor>) => boolean;
  getSelectedItems: () => { productKey: string }[];
  itemFilter: DropDownCategories;
  setItemFilter: SetState<DropDownCategories>;
  handleSelectedItemsAddToCart: () => Promise<void>;
  handleSingleItemAddToCart: (item: CartItem) => Promise<void>;
  handleConfirm: (callback: () => void | Promise<void>) => Promise<void>;
  listOptions: ShoppingListOption[];
  handleMoveToList: (targetListId: number) => Promise<void>;
  handleCopyToList: (targetListIds: number[]) => Promise<void>;
  handleDeleteSelected: () => Promise<void>;
  handleCompanySpecifiedAction: (isCompanySpecified: boolean) => Promise<void>;
  isUpdatingCart: boolean;
  isMovingFavoriteProducts: boolean;
  isCopyingFavoriteProducts: boolean;
  isDeletingFavoriteProducts: boolean;
  isUpdatingCompanySpecified: boolean;
  isSupplierUser: boolean;
  handleViewProductDetails: (e: MouseEvent, product: ShoppingListProduct) => Promise<void>;
  selectedProduct: ShoppingListProduct | null;
  setSelectedProduct: SetState<ShoppingListProduct | null>;
  getSelectedProductCatalogType: () => number | undefined;
  productDetails: ProductDetails | undefined;
  loadingProductDetails: boolean;
  fetchProductDetails: () => Promise<QueryObserverResult<ProductDetails | undefined>>;
  fetchProducts: () => Promise<QueryObserverResult<ShoppingListQueryResult | undefined>>;
  isFetchingProductDetails: boolean;
  isProductPanelOpen: boolean;
  handleCloseProductPanel: () => void;
  isSwapItemModalOpen: boolean;
  setIsSwapItemModalOpen: SetState<boolean>;
  handleCloseSwapItemPanel: () => void;
  swapItemData: RecommendPageParams | null;
  setSwapItemData: SetState<RecommendPageParams | null>;
  selectedCatalogFilter: SearchCatalogOption;
  setSelectedCatalogFilter: SetState<SearchCatalogOption>;
  catalogTreeData: CatalogTreeViewItem[];
  catalogDropdownPlaceholder: string;
  exportShoppingListToExcel: () => Promise<void>;
  isExportingShoppingList: boolean;
  isParEditable: boolean;
  exportProducts: ShoppingListProduct[];
  canReorderRows: boolean;
  onReorderProducts: (fromKey: string, toKey: string) => void;
}

export const ShoppingListProductsContext = createContext<ShoppingListProductsContextShape>(
  {} as ShoppingListProductsContextShape
);

export const ShoppingListProductsContextProvider: FC<PropsWithChildren> = ({
  children,
}): ReactElement => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const listId = Number(searchParams.get('listId') ?? 0);
  const isSupplierUser = usePermission({ permissions: IsSupplierUser });

  const { data: productListOptions = [] } = useGetFavoriteFoldersQuery(true);
  const { data: allFavoriteFolders = [] } = useGetAllFavoriteFoldersQuery();

  const shoppingListFolders = useMemo(
    () =>
      allFavoriteFolders.map((folder) => ({
        ...folder,
        id: Number(folder.id),
      })),
    [allFavoriteFolders]
  );

  const shoppingList = useMemo(
    () => shoppingListFolders.find((folder) => folder.id === listId),
    [listId, shoppingListFolders]
  );

  const isParEditable = useMemo(() => isShoppingListParEditable(shoppingList), [shoppingList]);

  const [selectedView, setSelectedView] = useState<number>(0);
  const [listViewTake, setListViewTake] = useState<number>(initialListViewDataState.take ?? 100);
  const [pictureViewTake, setPictureViewTake] = useState<number>(
    initialPictureViewDataState.take ?? 25
  );
  const listOptions = useMemo(
    () =>
      productListOptions
        .filter((folder) => folder.id !== listId && !folder.isShare)
        .map((folder) => ({
          id: folder.id,
          name: folder.description,
        })),
    [productListOptions, listId]
  );

  const [listViewSort, setListViewSort] = useState<Array<SortDescriptor>>(
    initialListViewDataState.sort ?? [{ field: 'sortOrder', dir: 'asc' }]
  );
  const [pictureViewSort, setPictureViewSort] = useState<Array<SortDescriptor>>(
    initialPictureViewDataState.sort ?? [{ field: 'sortOrder', dir: 'asc' }]
  );
  const [dataState, setDataState] = useState<State>({ ...initialListViewDataState });
  const [filterSearch, setFilterSearch] = useState('');
  const [productSelections, setProductSelections] = useState<
    Record<string, ShoppingListProductSelectionState>
  >({});
  const [reorderedProducts, setReorderedProducts] = useState<ShoppingListProduct[] | null>(null);
  const [itemFilter, setItemFilterState] = useState<DropDownCategories>(ProductFilterCategories[0]);
  const [pendingFilterToastKey, setPendingFilterToastKey] = useState<string | null>(null);
  const [selectedCatalogFilter, setSelectedCatalogFilter] = useState<SearchCatalogOption>(
    DEFAULT_SEARCH_CATALOG_OPTION
  );
  const { catalogs, isLoading: loadingCatalogs } = useGetCatalogsQuery();
  const catalogTreeData = useMemo(
    () =>
      transformCatalogsToTreeData(catalogs ?? [], {
        includeAllCatalogs: true,
        allCatalogsLabel: t('All Catalogs'),
      }),
    [catalogs, t]
  );
  const catalogDropdownPlaceholder = useMemo(
    () => getCatalogDropdownPlaceholder(catalogs, loadingCatalogs, t),
    [catalogs, loadingCatalogs, t]
  );
  const [isProductPanelOpen, setIsProductPanelOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ShoppingListProduct | null>(null);
  const [isFetchingProductDetails, setIsFetchingProductDetails] = useState(false);
  const isFetchingProductDetailsRef = useRef(false);
  const [isSwapItemModalOpen, setIsSwapItemModalOpen] = useState(false);
  const [swapItemData, setSwapItemData] = useState<RecommendPageParams | null>(null);
  const prevListIdRef = useRef(listId);
  const parUpdateInFlightRef = useRef<Set<string>>(new Set());
  const pendingParByProductKeyRef = useRef<Map<string, number>>(new Map());
  const reorderSaveInFlightRef = useRef(false);
  const selectedViewRef = useRef(selectedView);
  const listViewTakeRef = useRef(listViewTake);
  const pictureViewTakeRef = useRef(pictureViewTake);

  selectedViewRef.current = selectedView;
  listViewTakeRef.current = listViewTake;
  pictureViewTakeRef.current = pictureViewTake;

  const { mutateAsync: updateCart, isPending: isUpdatingCart } = useUpdateCartMutation({});
  const { mutateAsync: deleteFavoriteProducts, isPending: isDeletingFavoriteProducts } =
    useDeleteFavoriteProductsMutation({});
  const { mutateAsync: copyFavoriteProducts, isPending: isCopyingFavoriteProducts } =
    useCopyFavoriteProductsMutation({});
  const { mutateAsync: moveFavoriteProducts, isPending: isMovingFavoriteProducts } =
    useMoveFavoriteProductsMutation({});
  const { mutateAsync: updateCompanySpecified, isPending: isUpdatingCompanySpecified } =
    useUpdateCompanySpecifiedMutation({});
  const { mutateAsync: updateFavoriteProduct } = useUpdateFavoriteProductMutation({});

  const shoppingListRequest = useMemo(
    (): GetShoppingListRequest => ({
      folderIds: [listId],
      queryOptions: {
        clientSide: true,
        skip: 0,
        take: 0,
        skipCalculatingTotalRecordsCount: false,
        sort: [],
        filters: [],
      },
    }),
    [listId]
  );

  const {
    data: shoppingListResponse,
    isLoading: loadingShoppingList,
    isFetching: isFetchingShoppingList,
    isError: isShoppingListError,
    refetch: refetchShoppingList,
  } = useGetShoppingListQuery({
    request: shoppingListRequest,
    enabled: listId > 0,
  });

  useEffect(() => {
    if (isShoppingListError) {
      toast.error(t('Unable to load shopping list products. Please try again.'));
    }
  }, [isShoppingListError, t]);

  useEffect(() => {
    if (prevListIdRef.current === listId) {
      return;
    }
    prevListIdRef.current = listId;

    const view = selectedViewRef.current;
    const take = view === 0 ? listViewTakeRef.current : pictureViewTakeRef.current;

    setListViewSort(initialListViewDataState.sort ?? [{ field: 'sortOrder', dir: 'asc' }]);
    setPictureViewSort(initialPictureViewDataState.sort ?? [{ field: 'sortOrder', dir: 'asc' }]);

    if (view === 0) {
      setDataState({ ...initialListViewDataState, take });
    } else {
      setDataState({ ...initialPictureViewDataState, take });
    }
    setFilterSearch('');
    setItemFilterState(ProductFilterCategories[0]);
    setPendingFilterToastKey(null);
    setSelectedCatalogFilter(DEFAULT_SEARCH_CATALOG_OPTION);
    setProductSelections({});
    parUpdateInFlightRef.current.clear();
    pendingParByProductKeyRef.current.clear();
    setReorderedProducts(null);
    setIsProductPanelOpen(false);
    setSelectedProduct(null);
    setIsSwapItemModalOpen(false);
    setSwapItemData(null);
    queryClient.removeQueries({ queryKey: ['productDetails'] });
    removeInactiveShoppingListQueries(queryClient, listId);
  }, [listId, queryClient]);

  useEffect(() => {
    setReorderedProducts(null);
  }, [shoppingListResponse?.items]);

  const allProducts = useMemo(
    () => reorderedProducts ?? shoppingListResponse?.items ?? [],
    [reorderedProducts, shoppingListResponse?.items]
  );

  const exportProducts = useMemo(() => sortProductsForExport(allProducts), [allProducts]);

  const toolbarFilteredProducts = useMemo(() => {
    const searchFilteredProducts = applySearchTextFilter(allProducts, filterSearch);
    return applyToolbarFilters(
      searchFilteredProducts,
      itemFilter.value,
      selectedCatalogFilter.catalogId
    );
  }, [allProducts, filterSearch, itemFilter.value, selectedCatalogFilter.catalogId]);

  const attributesFilter = useMemo(
    () => extractAttributesFilter(dataState.filter),
    [dataState.filter]
  );
  const attributesFilterKey = useMemo(
    () => getAttributesFilterKey(dataState.filter),
    [dataState.filter]
  );
  const previousAttributesFilterKeyRef = useRef(attributesFilterKey);
  const itemFilterRef = useRef(itemFilter);
  itemFilterRef.current = itemFilter;

  useEffect(() => {
    const previousKey = previousAttributesFilterKeyRef.current;
    previousAttributesFilterKeyRef.current = attributesFilterKey;

    if (
      shouldResetItemFilterForAttributes(previousKey, attributesFilterKey) &&
      itemFilterRef.current.value === 1
    ) {
      setItemFilterState(ProductFilterCategories[0]);
      setPendingFilterToastKey('contractedItemsFilterReset');
    }
  }, [attributesFilterKey]);

  const setItemFilter = useCallback<SetState<DropDownCategories>>(
    (value) => {
      const nextValue = typeof value === 'function' ? value(itemFilterRef.current) : value;

      if (shouldClearAttributesForContractedItems(nextValue.value, attributesFilterKey)) {
        setDataState((prev) => ({
          ...prev,
          skip: 0,
          filter: removeFieldFromFilter(prev.filter, 'attributes') ?? EMPTY_GRID_FILTER,
        }));
        setPendingFilterToastKey('attributeFilterCleared');
      }

      setItemFilterState(nextValue);
    },
    [attributesFilterKey]
  );

  const gridFilterableProducts = useMemo(
    () => applyAttributesFilter(toolbarFilteredProducts, attributesFilter),
    [toolbarFilteredProducts, attributesFilter]
  );

  const processDataState = useMemo(() => buildShoppingListProcessState(dataState), [dataState]);

  const gridProducts = useMemo(
    () => prepareProductsForGrid(gridFilterableProducts),
    [gridFilterableProducts]
  );

  const processedProducts = useMemo(
    () => process(gridProducts, processDataState),
    [gridProducts, processDataState]
  );

  const products = processedProducts.data;
  const productsTotal = processedProducts.total;
  const loadingProducts =
    loadingShoppingList ||
    isFetchingShoppingList ||
    isUpdatingCart ||
    isDeletingFavoriteProducts ||
    isCopyingFavoriteProducts ||
    isMovingFavoriteProducts ||
    isUpdatingCompanySpecified;

  useEffect(() => {
    if (!pendingFilterToastKey || loadingProducts) {
      return;
    }

    toast.info(t(pendingFilterToastKey), { autoClose: 3000 });
    setPendingFilterToastKey(null);
  }, [pendingFilterToastKey, loadingProducts, t]);

  const canReorderRows = useMemo(
    () => Boolean(shoppingList) && !shoppingList?.isShare && selectedView === 0,
    [shoppingList, selectedView]
  );

  const onReorderProducts = useCallback(
    (fromKey: string, toKey: string) => {
      if (
        !canReorderRows ||
        !fromKey ||
        !toKey ||
        fromKey === toKey ||
        reorderSaveInFlightRef.current
      ) {
        return;
      }

      const source = reorderedProducts ?? shoppingListResponse?.items ?? [];
      // Reorder against the currently sorted visual order so DnD works while column sorting is active.
      const visualOrder = isSortOrderAscending(dataState.sort)
        ? source
        : process(source, { sort: dataState.sort }).data;
      const next = reorderShoppingListProducts(visualOrder, fromKey, toKey);

      if (next === visualOrder) {
        return;
      }

      setReorderedProducts(next);
      if (!isSortOrderAscending(dataState.sort)) {
        const defaultSort = initialListViewDataState.sort ?? [{ field: 'sortOrder', dir: 'asc' }];
        setListViewSort(defaultSort);
        setDataState((prev) => ({ ...prev, sort: defaultSort, skip: 0 }));
      }
      reorderSaveInFlightRef.current = true;

      void (async () => {
        try {
          await updateFavoriteProduct(buildReorderFavoriteProductRequest(listId, next));
        } catch {
          setReorderedProducts(source);
          toast.error(t('Unable to save the new order. Please try again.'));
        } finally {
          reorderSaveInFlightRef.current = false;
        }
      })();
    },
    [
      canReorderRows,
      reorderedProducts,
      shoppingListResponse?.items,
      dataState.sort,
      listId,
      updateFavoriteProduct,
      t,
    ]
  );

  useEffect(() => {
    setDataState((prev) => ({
      ...prev,
      skip: 0,
    }));
  }, [filterSearch, itemFilter.value, selectedCatalogFilter.catalogId]);

  const {
    data: apiProductDetails,
    isLoading: isLoadingProductDetails,
    isFetching: fetchingProductDetails,
    refetch: fetchProductDetails,
  } = useGetProductDetailsQuery(
    get(selectedProduct, 'productId', null),
    get(selectedProduct, 'catalogId', null),
    get(selectedProduct, 'categoryId', null)
  );

  const productDetails = apiProductDetails;
  const loadingProductDetails =
    isLoadingProductDetails || fetchingProductDetails || isFetchingProductDetails;

  const hasSelectedItems = useMemo(
    () => Object.values(productSelections).some((selection) => selection.selected),
    [productSelections]
  );

  const isDirtyData = useMemo(
    () => Object.values(productSelections).some((p) => p.qty !== 0),
    [productSelections]
  );

  usePostMessage({ isDirty: isDirtyData });

  const clearSelections = useCallback(() => {
    setProductSelections({});
  }, []);

  const getSelectedItems = useCallback(
    () =>
      Object.entries(productSelections)
        .filter(([, selection]) => selection.selected)
        .map(([productKey]) => ({ productKey })),
    [productSelections]
  );

  const shouldPreserveSort = useCallback((currentSortArray: Array<SortDescriptor>): boolean => {
    const fields = currentSortArray.map((sort) => sort.field).join('');
    return new RegExp(`(${SHOPPING_LIST_PRESERVE_SORT_FIELDS.join('|')})`, 'gi').test(fields);
  }, []);

  const getTakeForView = useCallback(
    (viewIndex: number): number => (viewIndex === 0 ? listViewTake : pictureViewTake),
    [listViewTake, pictureViewTake]
  );

  const getSortForView = useCallback(
    (viewIndex: number): Array<SortDescriptor> =>
      viewIndex === 0 ? listViewSort : pictureViewSort,
    [listViewSort, pictureViewSort]
  );

  const hasActiveFilters = useMemo(() => {
    const filters = dataState?.filter?.filters;
    const hasGridFilters =
      filters?.some((filter: CompositeFilterDescriptor | FilterDescriptor) => {
        if (!filter) {
          return false;
        }

        return 'filters' in filter ? filter.filters.length > 0 : true;
      }) ?? false;
    const hasSearch = filterSearch.trim().length > 0;
    const hasItemFilter = itemFilter.value !== 0;
    const hasCatalogFilter = selectedCatalogFilter.catalogId !== 'all';
    const defaultSort = (
      selectedView === 0 ? initialListViewDataState : initialPictureViewDataState
    ).sort?.[0];
    const currentSort = dataState?.sort?.[0];
    const hasNonDefaultSort =
      currentSort !== undefined &&
      (currentSort.field !== defaultSort?.field || currentSort.dir !== defaultSort?.dir);

    return hasGridFilters || hasSearch || hasItemFilter || hasCatalogFilter || hasNonDefaultSort;
  }, [
    dataState?.filter?.filters,
    dataState?.sort,
    filterSearch,
    itemFilter.value,
    selectedCatalogFilter.catalogId,
    selectedView,
  ]);

  const clearFilters = useCallback(() => {
    setFilterSearch('');
    setItemFilterState(ProductFilterCategories[0]);
    setPendingFilterToastKey(null);
    setSelectedCatalogFilter(DEFAULT_SEARCH_CATALOG_OPTION);
    setListViewSort(initialListViewDataState.sort ?? [{ field: 'sortOrder', dir: 'asc' }]);
    setPictureViewSort(initialPictureViewDataState.sort ?? [{ field: 'sortOrder', dir: 'asc' }]);
    if (selectedView === 0) {
      setDataState((prev) => ({
        ...initialListViewDataState,
        take: prev.take,
      }));
    } else {
      setDataState((prev) => ({
        ...initialPictureViewDataState,
        take: prev.take,
      }));
    }
  }, [selectedView]);

  const appendShoppingListGlCodeToCartItems = useCallback(
    (items: CartItem[]): CartItem[] => {
      const glCode = shoppingList?.glCode;
      if (!glCode) {
        return items;
      }

      return items.map((item) => ({ ...item, glCode }));
    },
    [shoppingList?.glCode]
  );

  const buildCartItems = useCallback(
    (): CartItem[] =>
      Object.entries(productSelections)
        .filter(([, selection]) => selection.selected && selection.qty !== 0)
        .map(([productKey, selection]) => {
          const product = allProducts.find((item) => item.productKey === productKey);
          if (!product) {
            throw new Error('Product not found');
          }
          return {
            productId: product.productId,
            catalogId: product.catalogId,
            categoryId: product.categoryId,
            quantity: selection.qty,
            isCompanySpecified: product.isFormulary,
          };
        }),
    [allProducts, productSelections]
  );

  const handleSelectedItemsAddToCart = useCallback(async () => {
    const items = appendShoppingListGlCodeToCartItems(buildCartItems());
    if (items.length === 0) {
      return;
    }

    await updateCart({ items });
    handlePostMessage({ cartUpdated: true });
    toast.success(t('searchProductsAddedToCartSuccess', { count: items.length }));
    clearSelections();
    await refetchShoppingList();
  }, [
    appendShoppingListGlCodeToCartItems,
    buildCartItems,
    clearSelections,
    refetchShoppingList,
    t,
    updateCart,
  ]);

  const {
    isOpen: isUnsavedSelectionOpen,
    handleConfirm: handleUnsavedConfirm,
    handleAddToCart: handleUnsavedAddToCart,
    handleClose: handleUnsavedClose,
    confirm: confirmUnsavedSelection,
    selectedItemsCount,
  } = useUnsavedSelection({
    productSelections,
    onAddToCart: handleSelectedItemsAddToCart,
  });

  const handleUnsavedClearAndConfirm = useCallback(async () => {
    clearSelections();
    await handleUnsavedConfirm();
  }, [clearSelections, handleUnsavedConfirm]);

  const handleConfirm = useCallback(
    async (callback: () => void | Promise<void>): Promise<void> => {
      await confirmUnsavedSelection(callback);
    },
    [confirmUnsavedSelection]
  );

  const selectShoppingList = useCallback(
    (folder: FavoriteFolder) => {
      const nextListId = Number(folder?.id);
      if (!Number.isFinite(nextListId) || nextListId <= 0 || nextListId === listId) {
        return;
      }

      void handleConfirm(() => {
        setSearchParams(
          (prev) => {
            const nextParams = new URLSearchParams(prev);
            nextParams.set('listId', String(nextListId));
            return nextParams;
          },
          { replace: false }
        );
      });
    },
    [handleConfirm, listId, setSearchParams]
  );

  const handleFilterSearchChange = useCallback(
    async (value: string) => {
      await handleConfirm(() => {
        setFilterSearch(value);
        setDataState((prev) => ({ ...prev, skip: 0 }));
      });
    },
    [handleConfirm]
  );

  const onDataStateChange = useCallback(
    async (event: GridDataStateChangeEvent | State) => {
      const newDataState = 'dataState' in event ? event.dataState : event;
      await handleConfirm(() => {
        setProductSelections({});
        if (newDataState) {
          setDataState((prev) => {
            const updated = { ...prev, ...newDataState };
            if (newDataState.take !== undefined && newDataState.take !== prev.take) {
              if (selectedView === 0) {
                setListViewTake(newDataState.take);
              } else {
                setPictureViewTake(newDataState.take);
              }
            }
            if (newDataState.sort !== undefined) {
              if (selectedView === 0) {
                setListViewSort(newDataState.sort);
              } else {
                setPictureViewSort(newDataState.sort);
              }
            }
            return updated;
          });
        }
      });
    },
    [handleConfirm, selectedView]
  );

  const onSelectionChange = useCallback((event: GridSelectionChangeEvent) => {
    const productKey = event?.dataItem?.productKey;
    if (!productKey) return;

    setProductSelections((prev) => {
      const current = prev[productKey] || { selected: false, qty: 0 };
      const newSelected = !current.selected;
      return {
        ...prev,
        [productKey]: {
          selected: newSelected,
          qty: newSelected && current.qty === 0 ? 1 : newSelected ? current.qty : 0,
        },
      };
    });
  }, []);

  const onItemChange = useCallback(
    (event: GridItemChangeEvent) => {
      const { dataItem, field, value } = event;
      const productKey = dataItem.productKey as string;

      if (field === 'qty') {
        const qty = Number(value) || 0;
        setProductSelections((prev) => ({
          ...prev,
          [productKey]: {
            selected: qty !== 0,
            qty,
          },
        }));
        return;
      }

      if (field === 'par') {
        if (!isParEditable) {
          return;
        }

        const par = Number(value) || 0;
        const product = allProducts.find((item) => item.productKey === productKey);

        if (!product) {
          return;
        }

        const currentPar =
          pendingParByProductKeyRef.current.get(productKey) ?? Number(product.par ?? 0);

        if (currentPar === par || parUpdateInFlightRef.current.has(productKey)) {
          return;
        }

        parUpdateInFlightRef.current.add(productKey);
        pendingParByProductKeyRef.current.set(productKey, par);

        void (async () => {
          try {
            await updateFavoriteProduct(buildUpdateFavoriteProductRequest(listId, product, par));
            toast.success(t('shoppingList.parUpdatedSuccess', { itemNumber: product.itemNum }));
          } catch {
            pendingParByProductKeyRef.current.delete(productKey);
            toast.error(t('Unable to update PAR. Please try again.'));
            await refetchShoppingList();
          } finally {
            parUpdateInFlightRef.current.delete(productKey);
          }
        })();
      }
    },
    [allProducts, isParEditable, listId, refetchShoppingList, t, updateFavoriteProduct]
  );

  const getProductSelection = useCallback(
    (productKey: string): ShoppingListProductSelectionState =>
      productSelections[productKey] || { selected: false, qty: 0 },
    [productSelections]
  );

  const updateProductSelection = useCallback(
    (productKey: string, updates: Partial<ShoppingListProductSelectionState>) => {
      setProductSelections((prev) => ({
        ...prev,
        [productKey]: {
          ...(prev[productKey] || { selected: false, qty: 0 }),
          ...updates,
        },
      }));
    },
    []
  );

  const handleSingleItemAddToCart = useCallback(
    async (item: CartItem) => {
      await updateCart({ items: appendShoppingListGlCodeToCartItems([item]) });
      handlePostMessage({ cartUpdated: true });
      toast.success(t('searchProductsAddedToCartSuccess', { count: 1 }));
      // Only the added item is deselected; the toolbar bulk add is what clears everything.
      setProductSelections((prev) => removeProductSelection(prev, getCartItemProductKey(item)));
      await refetchShoppingList();
    },
    [appendShoppingListGlCodeToCartItems, refetchShoppingList, t, updateCart]
  );

  const getSelectedShoppingListProducts = useCallback(
    () =>
      allProducts.filter((product) =>
        Object.entries(productSelections).some(
          ([productKey, selection]) => selection.selected && productKey === product.productKey
        )
      ),
    [allProducts, productSelections]
  );

  const handleCloseProductPanel = useCallback(() => {
    setIsProductPanelOpen(false);
    queryClient.removeQueries({ queryKey: ['productDetails'] });
    setSelectedProduct(null);
  }, [queryClient]);

  const handleCloseSwapItemPanel = useCallback(() => {
    setIsSwapItemModalOpen(false);
    setSwapItemData(null);
  }, []);

  const handleMoveToList = useCallback(
    async (targetListId: number) => {
      try {
        const selectedProducts = getSelectedShoppingListProducts();

        if (selectedProducts.length === 0) {
          toast.error(t('Please select at least one item to Move to'));
          return;
        }

        const items = selectedProducts.map((product) => ({
          productId: product.productId,
          catalogId: product.catalogId,
          categoryId: product.categoryId,
        }));

        await moveFavoriteProducts({
          fromFolder: listId,
          toFolder: targetListId,
          items,
        });

        toast.success(t('Item(s) Moved Successfully'));
        clearSelections();

        if (
          isProductPanelOpen &&
          selectedProduct &&
          selectedProducts.some((product) => product.productKey === selectedProduct.productKey)
        ) {
          handleCloseProductPanel();
        }
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          t('Unable to move selected item(s). Please try again.');
        toast.error(errorMessage);
      }
    },
    [
      getSelectedShoppingListProducts,
      moveFavoriteProducts,
      listId,
      clearSelections,
      isProductPanelOpen,
      selectedProduct,
      handleCloseProductPanel,
      t,
    ]
  );

  const handleCopyToList = useCallback(
    async (targetListIds: number[]) => {
      try {
        const selectedProducts = getSelectedShoppingListProducts();

        if (selectedProducts.length === 0) {
          toast.error(t('Please select at least one item to Copy to'));
          return;
        }

        if (targetListIds.length === 0) {
          toast.error(t('Please select at least one list'));
          return;
        }

        const items = selectedProducts.map((product) => product.productId);

        await copyFavoriteProducts({
          fromFolder: listId,
          toFolders: targetListIds,
          items,
        });

        toast.success(t('Item(s) Copied Successfully'));
        clearSelections();
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          t('Unable to copy selected item(s). Please try again.');
        toast.error(errorMessage);
      }
    },
    [getSelectedShoppingListProducts, copyFavoriteProducts, listId, clearSelections, t]
  );

  const handleDeleteSelected = useCallback(async () => {
    try {
      const selectedProducts = getSelectedShoppingListProducts();

      if (selectedProducts.length === 0) {
        toast.error(t('Please select at least one item'));
        return;
      }

      const isDeleteAccepted = await confirm({
        title: t('deleteConfirmationTitle'),
        content: t('Are you sure you want to Delete the selected item(s)?'),
        confirmButtonText: t('yesDelete'),
        cancelButtonText: t('noCancel'),
      });

      if (!isDeleteAccepted) {
        return;
      }

      const items = selectedProducts.map((product) => ({
        productId: product.productId,
        catalogId: product.catalogId,
        categoryId: product.categoryId,
      }));

      await deleteFavoriteProducts({
        fromFolder: listId,
        items,
        deletedCount: items.length,
      });

      toast.success(t('Item(s) Deleted Successfully'));
      clearSelections();

      if (
        isProductPanelOpen &&
        selectedProduct &&
        selectedProducts.some((product) => product.productKey === selectedProduct.productKey)
      ) {
        handleCloseProductPanel();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        t('Unable to delete selected item(s). Please try again.');
      toast.error(errorMessage);
    }
  }, [
    getSelectedShoppingListProducts,
    confirm,
    deleteFavoriteProducts,
    listId,
    clearSelections,
    isProductPanelOpen,
    selectedProduct,
    handleCloseProductPanel,
    t,
  ]);

  const handleCompanySpecifiedAction = useCallback(
    async (isCompanySpecified: boolean) => {
      try {
        const selectedProducts = getSelectedShoppingListProducts();

        if (selectedProducts.length === 0) {
          toast.error(t('Please select at least one item to Company Specified'));
          return;
        }

        const items = selectedProducts.map((product) => ({
          favoriteCategoryId: listId,
          productId: product.productId,
          catalogId: product.catalogId,
          categoryId: product.categoryId,
        }));

        await updateCompanySpecified({
          isCompanySpecified,
          items,
        });

        toast.success(
          isCompanySpecified
            ? t('Item(s) Marked as Company Specified Successfully')
            : t('Item(s) Unmarked as Company Specified Successfully')
        );
        clearSelections();
        await queryClient.invalidateQueries({
          queryKey: queryKeys.favorite.shoppingList(shoppingListRequest),
        });
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          t('Unable to update company specified status. Please try again.');
        toast.error(errorMessage);
      }
    },
    [
      getSelectedShoppingListProducts,
      updateCompanySpecified,
      listId,
      clearSelections,
      queryClient,
      shoppingListRequest,
      t,
    ]
  );

  const handleViewProductDetails = useCallback(
    async (e: MouseEvent, product: ShoppingListProduct) => {
      e.preventDefault();
      e.stopPropagation();

      if (isFetchingProductDetailsRef.current || loadingProductDetails) {
        return;
      }

      try {
        isFetchingProductDetailsRef.current = true;
        setIsFetchingProductDetails(true);

        await queryClient.cancelQueries({
          queryKey: [...queryKeys.product.root, 'productDetails'],
        });

        setSelectedProduct(product);

        await queryClient.fetchQuery({
          queryKey: queryKeys.product.productDetails(
            product.productId,
            product.catalogId,
            product.categoryId
          ),
          queryFn: fetchProductDetailsQuery,
        });

        setIsProductPanelOpen(true);
      } catch (error) {
        if ((error as Error)?.name !== 'CancelledError') {
          console.error('Failed to fetch product details:', error);
        }
      } finally {
        isFetchingProductDetailsRef.current = false;
        setIsFetchingProductDetails(false);
      }
    },
    [loadingProductDetails, queryClient]
  );

  const getSelectedProductCatalogType = useCallback(
    () => (selectedProduct?.isOfflineSupplierItem ? 3 : undefined),
    [selectedProduct]
  );

  const fetchProducts = useCallback(async () => refetchShoppingList(), [refetchShoppingList]);

  const [isExportingShoppingList, setIsExportingShoppingList] = useState(false);

  const exportShoppingListToExcel = useCallback(async () => {
    if (!listId || listId <= 0) {
      toast.error(t('Failed to export shopping list(s)'));
      return;
    }

    setIsExportingShoppingList(true);
    try {
      const sections = await fetchShoppingListsForExport([
        {
          folderId: listId,
          listName: shoppingList?.description?.trim() || 'Shopping List',
        },
      ]);
      await exportShoppingListsToExcel(sections, t);
    } catch {
      toast.error(t('Failed to export shopping list(s)'));
    } finally {
      setIsExportingShoppingList(false);
    }
  }, [listId, shoppingList?.description, t]);

  const contextValue = useMemo<ShoppingListProductsContextShape>(
    () => ({
      listId,
      shoppingList,
      shoppingListFolders,
      selectShoppingList,
      selectedView,
      setSelectedView,
      dataState,
      gridDataState: processDataState,
      setDataState,
      onDataStateChange,
      products,
      sourceProducts: gridProducts,
      productsTotal,
      loadingProducts,
      filterSearch,
      handleFilterSearchChange,
      productSelections,
      setProductSelections,
      onSelectionChange,
      onItemChange,
      getProductSelection,
      updateProductSelection,
      hasSelectedItems,
      clearSelections,
      clearFilters,
      hasActiveFilters,
      getTakeForView,
      getSortForView,
      shouldPreserveSort,
      getSelectedItems,
      itemFilter,
      setItemFilter,
      handleSelectedItemsAddToCart,
      handleSingleItemAddToCart,
      handleConfirm,
      listOptions,
      handleMoveToList,
      handleCopyToList,
      handleDeleteSelected,
      handleCompanySpecifiedAction,
      isUpdatingCart,
      isMovingFavoriteProducts,
      isCopyingFavoriteProducts,
      isDeletingFavoriteProducts,
      isUpdatingCompanySpecified,
      isSupplierUser,
      handleViewProductDetails,
      selectedProduct,
      setSelectedProduct,
      getSelectedProductCatalogType,
      productDetails,
      loadingProductDetails,
      fetchProductDetails,
      fetchProducts,
      isFetchingProductDetails,
      isProductPanelOpen,
      handleCloseProductPanel,
      isSwapItemModalOpen,
      setIsSwapItemModalOpen,
      handleCloseSwapItemPanel,
      swapItemData,
      setSwapItemData,
      selectedCatalogFilter,
      setSelectedCatalogFilter,
      catalogTreeData,
      catalogDropdownPlaceholder,
      exportShoppingListToExcel,
      isExportingShoppingList,
      exportProducts,
      isParEditable,
      canReorderRows,
      onReorderProducts,
    }),
    [
      listId,
      shoppingList,
      shoppingListFolders,
      selectShoppingList,
      selectedView,
      dataState,
      processDataState,
      products,
      gridProducts,
      productsTotal,
      loadingProducts,
      filterSearch,
      handleFilterSearchChange,
      productSelections,
      onSelectionChange,
      onItemChange,
      getProductSelection,
      updateProductSelection,
      hasSelectedItems,
      clearSelections,
      clearFilters,
      hasActiveFilters,
      getTakeForView,
      getSortForView,
      shouldPreserveSort,
      getSelectedItems,
      itemFilter,
      setItemFilter,
      handleSelectedItemsAddToCart,
      handleSingleItemAddToCart,
      handleConfirm,
      listOptions,
      handleMoveToList,
      handleCopyToList,
      handleDeleteSelected,
      handleCompanySpecifiedAction,
      isUpdatingCart,
      isMovingFavoriteProducts,
      isCopyingFavoriteProducts,
      isDeletingFavoriteProducts,
      isUpdatingCompanySpecified,
      isSupplierUser,
      handleViewProductDetails,
      selectedProduct,
      getSelectedProductCatalogType,
      productDetails,
      loadingProductDetails,
      fetchProductDetails,
      fetchProducts,
      isFetchingProductDetails,
      isProductPanelOpen,
      handleCloseProductPanel,
      isSwapItemModalOpen,
      handleCloseSwapItemPanel,
      swapItemData,
      selectedCatalogFilter,
      catalogTreeData,
      catalogDropdownPlaceholder,
      exportShoppingListToExcel,
      isExportingShoppingList,
      exportProducts,
      isParEditable,
      onDataStateChange,
      canReorderRows,
      onReorderProducts,
    ]
  );

  return (
    <ShoppingListProductsContext.Provider value={contextValue}>
      {children}
      <UnsavedSelectionDialog
        open={isUnsavedSelectionOpen}
        onClose={handleUnsavedClose}
        onConfirm={handleUnsavedClearAndConfirm}
        onAddToCart={handleUnsavedAddToCart}
        selectedItemsCount={selectedItemsCount}
      />
    </ShoppingListProductsContext.Provider>
  );
};
